import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getMainImage, getImageByColor } from '../../../utils/image-utils';
import { useNotification } from '../../shared/contexts/NotificationContext';
import { getApiEndpoint } from '../../shared/utils/api-url';

interface Product {
  id: number;
  name: string;
  description?: string;
  price: number;
  original_price?: number;
  images?: { main: string; colors?: { [color: string]: string } };
  colors?: string[];
  sizes?: string[];
  discount_percent?: number;
  stock?: number;
}

function Produto() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { error: showError } = useNotification();
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedColor, setSelectedColor] = useState<string>('');
  const [selectedSize, setSelectedSize] = useState<string>('');
  const [activeTab, setActiveTab] = useState<'details' | 'care' | 'shipping'>('details');
  const [mainImage, setMainImage] = useState<string>('');

  useEffect(() => {
    if (id) {
      loadProduct(parseInt(id));
    }
  }, [id]);

  useEffect(() => {
    if (product) {
      updateMainImage();
    }
  }, [product, selectedColor]);

  const loadProduct = async (productId: number) => {
    setLoading(true);
    try {
      const response = await fetch(getApiEndpoint(`/products/${productId}`));
      const data = await response.json();
      
      // Parse images if it's a string
      if (typeof data.images === 'string') {
        try {
          data.images = JSON.parse(data.images);
        } catch (e) {
          data.images = { main: data.images || '' };
        }
      }
      
      // Parse colors and sizes if they're strings
      if (typeof data.colors === 'string') {
        try {
          data.colors = JSON.parse(data.colors);
        } catch (e) {
          data.colors = data.colors ? [data.colors] : [];
        }
      }
      if (typeof data.sizes === 'string') {
        try {
          data.sizes = JSON.parse(data.sizes);
        } catch (e) {
          data.sizes = data.sizes ? [data.sizes] : [];
        }
      }

      setProduct(data);
      if (data.colors && data.colors.length > 0) {
        setSelectedColor(data.colors[0]);
      }
      if (data.sizes && data.sizes.length > 0) {
        setSelectedSize(data.sizes[0]);
      }
    } catch (error) {
      console.error('Erro ao carregar produto:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateMainImage = () => {
    if (!product) return;
    
    if (selectedColor && product.images?.colors?.[selectedColor]) {
      setMainImage(getImageByColor(product.images, selectedColor));
    } else {
      setMainImage(getMainImage(product.images));
    }
  };

  const handleAddToCart = () => {
    if (!product) return;
    
    // Verificar se precisa selecionar cor/tamanho apenas se eles existirem
    const hasColors = product.colors && product.colors.length > 0;
    const hasSizes = product.sizes && product.sizes.length > 0;
    
    if (hasColors && !selectedColor) {
      showError('Por favor, selecione uma cor');
      return;
    }
    
    if (hasSizes && !selectedSize) {
      showError('Por favor, selecione um tamanho');
      return;
    }
    
    // Adicionar ao carrinho (localStorage por enquanto)
    const cartItem = {
      id: Date.now(),
      product_id: product.id,
      name: product.name,
      price: product.price,
      color: selectedColor || null,
      size: selectedSize || null,
      quantity: 1,
      image: mainImage || getMainImage(product.images),
    };
    
    const existingCart = JSON.parse(localStorage.getItem('cart') || '[]');
    existingCart.push(cartItem);
    localStorage.setItem('cart', JSON.stringify(existingCart));
    
    navigate('/carrinho');
  };

  const handleBuyNow = () => {
    if (!product) return;
    
    // Verificar se precisa selecionar cor/tamanho apenas se eles existirem
    const hasColors = product.colors && product.colors.length > 0;
    const hasSizes = product.sizes && product.sizes.length > 0;
    
    if (hasColors && !selectedColor) {
      showError('Por favor, selecione uma cor');
      return;
    }
    
    if (hasSizes && !selectedSize) {
      showError('Por favor, selecione um tamanho');
      return;
    }
    
    // Adicionar ao carrinho e ir direto para checkout
    const cartItem = {
      id: Date.now(),
      product_id: product.id,
      name: product.name,
      price: product.price,
      color: selectedColor || null,
      size: selectedSize || null,
      quantity: 1,
      image: mainImage || getMainImage(product.images),
    };
    
    // Limpar carrinho e adicionar apenas este item
    localStorage.setItem('cart', JSON.stringify([cartItem]));
    
    navigate('/checkout');
  };

  const formatPrice = (price: number) => {
    return `R$ ${price.toFixed(2).replace('.', ',')}`;
  };

  const calculateDiscount = (price: number, originalPrice: number) => {
    return Math.round(((originalPrice - price) / originalPrice) * 100);
  };

  if (loading) {
    return (
      <div className="mobile-content">
        <div className="loading-message">Carregando produto...</div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="mobile-content">
        <div className="empty-message">Produto não encontrado</div>
      </div>
    );
  }

  const discount = product.original_price && product.price < product.original_price
    ? calculateDiscount(product.price, product.original_price)
    : null;

  return (
    <div className="mobile-content">
      <div className="product-page-layout">
        {/* Mobile: Preview First | Desktop: Preview Right */}
        <div className="product-preview-column">
          <section className="product-images">
            <div className="main-image">
              <img
                src={mainImage || getMainImage(product.images)}
                alt={product.name}
                id="main-image"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src = `data:image/svg+xml,%3Csvg width='400' height='400' xmlns='http://www.w3.org/2000/svg'%3E%3Crect width='400' height='400' fill='%23f0f0f0'/%3E%3Ctext x='50%25' y='50%25' text-anchor='middle' dominant-baseline='middle' font-family='Arial, sans-serif' font-size='16' fill='%23999'%3EProduto%3C/text%3E%3C/svg%3E`;
                }}
              />
              {discount && (
                <div className="image-badge">
                  <span>-{discount}%</span>
                </div>
              )}
            </div>
            {/* Thumbnails - se houver múltiplas imagens */}
            {product.images && product.images.colors && Object.keys(product.images.colors).length > 0 && (
              <div className="thumbnails">
                <img
                  src={getMainImage(product.images)}
                  alt={product.name}
                  className={`thumbnail ${!selectedColor ? 'active' : ''}`}
                  onClick={() => {
                    setSelectedColor('');
                    setMainImage(getMainImage(product.images));
                  }}
                />
                {Object.entries(product.images.colors).map(([color, imageUrl]) => (
                  <img
                    key={color}
                    src={imageUrl as string}
                    alt={`${product.name} - ${color}`}
                    className={`thumbnail ${selectedColor === color ? 'active' : ''}`}
                    onClick={() => {
                      setSelectedColor(color);
                      setMainImage(imageUrl as string);
                    }}
                  />
                ))}
              </div>
            )}
          </section>

          {/* Color/Size Selection Info */}
          {(selectedColor || selectedSize) && (
            <section className="product-selection-info">
              {selectedColor && (
                <div className="selection-info-item">
                  <span className="selection-label">Cor selecionada:</span>
                  <span className="selection-value">{selectedColor}</span>
                </div>
              )}
              {selectedSize && (
                <div className="selection-info-item">
                  <span className="selection-label">Tamanho selecionado:</span>
                  <span className="selection-value">{selectedSize}</span>
                </div>
              )}
            </section>
          )}
        </div>

        {/* Mobile: Info Second | Desktop: Info Left */}
        <div className="product-info-column">
          {/* Product Info */}
          <section className="product-info">
        <div className="product-header">
          <h1 className="product-title">{product.name}</h1>
          {discount && <div className="product-badge">-{discount}%</div>}
        </div>
        <div className="product-price">
          <span className="current-price">{formatPrice(product.price)}</span>
          {product.original_price && product.original_price > product.price && (
            <span className="old-price">{formatPrice(product.original_price)}</span>
          )}
        </div>
        <p className="product-description">
          {product.description || 'Produto de alta qualidade, cuidadosamente selecionado para oferecer o melhor em conforto e estilo.'}
        </p>

        {/* Options - Mostrar apenas se existirem cores ou tamanhos */}
        <div className="product-options">
          {/* Cores */}
          {product.colors && product.colors.length > 0 && (
            <div className="option-group">
              <label className="option-label">
                Cor {!selectedColor && <span className="required-indicator">*</span>}
              </label>
              <div className="option-buttons">
                {product.colors.map((color) => (
                  <button
                    key={color}
                    type="button"
                    className={`option-btn color-option ${selectedColor === color ? 'active' : ''}`}
                    onClick={() => setSelectedColor(color)}
                    title={color}
                  >
                    <span className="color-name">{color}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Tamanhos */}
          {product.sizes && product.sizes.length > 0 && (
            <div className="option-group">
              <label className="option-label">
                Tamanho {!selectedSize && <span className="required-indicator">*</span>}
              </label>
              <div className="option-buttons">
                {product.sizes.map((size) => (
                  <button
                    key={size}
                    type="button"
                    className={`option-btn size-option ${selectedSize === size ? 'active' : ''}`}
                    onClick={() => setSelectedSize(size)}
                  >
                    {size}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Stock Information */}
        {product.stock !== undefined && (
          <div className="product-stock">
            {product.stock > 0 ? (
              <div className="stock-label stock-available">
                <i className="fas fa-check-circle"></i>
                <span>Em estoque ({product.stock} unidades)</span>
              </div>
            ) : (
              <div className="stock-label stock-unavailable">
                <i className="fas fa-times-circle"></i>
                <span>Sem estoque</span>
              </div>
            )}
          </div>
        )}

        {/* Product Availability */}
        <div className="product-availability">
          <div className="availability-item">
            <i className="fas fa-truck"></i>
            <span>Frete grátis acima de R$ 200</span>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="product-actions">
          <button 
            className="buy-now-btn" 
            onClick={handleBuyNow}
            disabled={
              (product.stock !== undefined && product.stock <= 0) ||
              (product.colors && product.colors.length > 0 && !selectedColor) ||
              (product.sizes && product.sizes.length > 0 && !selectedSize)
            }
          >
            <i className="fas fa-credit-card"></i>
            Comprar Agora
          </button>
          <button 
            className="add-to-cart-btn" 
            onClick={handleAddToCart}
            disabled={
              (product.stock !== undefined && product.stock <= 0) ||
              (product.colors && product.colors.length > 0 && !selectedColor) ||
              (product.sizes && product.sizes.length > 0 && !selectedSize)
            }
          >
            <i className="fas fa-shopping-bag"></i>
            Adicionar ao Carrinho
          </button>
        </div>

        {/* Selection Warning */}
        {((product.colors && product.colors.length > 0 && !selectedColor) ||
          (product.sizes && product.sizes.length > 0 && !selectedSize)) && (
          <div className="selection-warning">
            <i className="fas fa-exclamation-triangle"></i>
            <span>
              {product.colors && product.colors.length > 0 && !selectedColor && 'Por favor, selecione uma cor'}
              {product.colors && product.colors.length > 0 && !selectedColor && 
               product.sizes && product.sizes.length > 0 && !selectedSize && ' e '}
              {product.sizes && product.sizes.length > 0 && !selectedSize && 'um tamanho'}
              {' antes de continuar'}
            </span>
          </div>
        )}
          </section>

          {/* Product Tabs */}
          <section className="product-tabs">
        <div className="tab-buttons">
          <button
            type="button"
            className={`tab-btn ${activeTab === 'details' ? 'active' : ''}`}
            onClick={() => setActiveTab('details')}
          >
            Detalhes
          </button>
          <button
            type="button"
            className={`tab-btn ${activeTab === 'care' ? 'active' : ''}`}
            onClick={() => setActiveTab('care')}
          >
            Cuidados
          </button>
          <button
            type="button"
            className={`tab-btn ${activeTab === 'shipping' ? 'active' : ''}`}
            onClick={() => setActiveTab('shipping')}
          >
            Entrega
          </button>
        </div>
        <div className={`tab-content ${activeTab === 'details' ? 'active' : ''}`} id="tab-details">
          <p>{product.description || 'Produto de alta qualidade, cuidadosamente selecionado para oferecer o melhor em conforto e estilo.'}</p>
        </div>
        <div className={`tab-content ${activeTab === 'care' ? 'active' : ''}`} id="tab-care">
          <p>Lavagem à mão ou máquina no ciclo delicado. Não usar alvejante. Passar em temperatura média.</p>
        </div>
        <div className={`tab-content ${activeTab === 'shipping' ? 'active' : ''}`} id="tab-shipping">
          <p>Entrega em todo o Brasil. Frete grátis para compras acima de R$ 200. Prazo de 5 a 10 dias úteis.</p>
          </div>
        </section>
        </div>
      </div>
    </div>
  );
}

export default Produto;

